import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Cpu, Plus, Sparkles, Upload, X } from 'lucide-react';
import { ACCEPT, PCB_ACCEPT, UNITS, modelsApi } from '../../services/modelsApi';
import { Card, Empty } from '../ProjectPage/ui';

// Documents 3D du projet (onglet Documents) : modèles (STEP, GLB, STL, OBJ) et jumeaux numériques (page HTML),
// versionnés, partageables au client. Même carte pour les cartes électroniques (pcb = true) : un zip du projet KiCad
// et de ses exports par version. Dépôt ici ; tout le reste sur la page du document.
const day = (iso) => (iso ? new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(iso)) : '');
const extension = (name = '') => name.toLowerCase().split('.').pop();
export const needsUnit = (name) => ['stl', 'obj'].includes(extension(name));
const isTwin = (name) => ['html', 'htm'].includes(extension(name));

export const UploadDialog = ({ projectId, modelId = null, pcb = false, onClose, onDone }) => {
  const input = useRef(null);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [label, setLabel] = useState('');
  const [unit, setUnit] = useState('mm');
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { input.current?.click(); }, []);
  const choose = (chosen) => {
    if (!chosen) return;
    setFile(chosen);
    setError('');
    if (!modelId) setTitle(chosen.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' '));
  };
  const send = async (event) => {
    event.preventDefault();
    if (!file) return;
    setError('');
    setProgress(0);
    try {
      const made = await modelsApi.upload(projectId, file, { title, unit, modelId, label }, (value) => setProgress(value));
      onDone(made);
    } catch (err) {
      setError(err.message || 'Dépôt impossible.');
      setProgress(null);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" role="dialog" aria-modal="true">
      <form onSubmit={send} className="w-full max-w-lg space-y-4 rounded-2xl bg-white p-5 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-slate-900">{modelId ? 'Nouvelle version' : (pcb ? 'Déposer une carte électronique' : 'Déposer un modèle 3D ou un jumeau')}</h2>
            <p className="text-xs text-slate-500">{pcb
              ? 'Un zip : le projet KiCad (.kicad_pro, .kicad_sch, .kicad_pcb) et ses exports — schéma PDF, vues du routage (SVG), STEP, Gerber et BOM. 150 Mo au plus.'
              : 'STEP, GLB, glTF, STL ou OBJ pour un modèle 3D, page HTML pour un jumeau numérique : 150 Mo au plus.'}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Fermer" className="rounded-lg p-1 text-slate-400 hover:bg-slate-100"><X size={16} /></button>
        </div>
        <input ref={input} type="file" accept={pcb ? PCB_ACCEPT : ACCEPT} className="hidden" onChange={(event) => choose(event.target.files?.[0])} />
        <button type="button" onClick={() => input.current?.click()}
          className="flex w-full items-center gap-3 rounded-xl border border-dashed border-slate-300 px-4 py-4 text-left text-sm hover:border-indigo-400">
          <Upload size={18} className="text-slate-400" />
          <span className="min-w-0">
            <span className="block truncate font-semibold text-slate-800">{file ? file.name : 'Choisir un fichier'}</span>
            {file && <span className="block text-xs text-slate-500">{(file.size / 1024 / 1024).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} Mo{pcb ? ' · carte électronique' : (isTwin(file.name) ? ' · jumeau numérique' : ' · modèle 3D')}</span>}
          </span>
        </button>
        {!modelId && (
          <label className="block text-sm">
            <span className="mb-1 block text-xs font-semibold text-slate-600">Titre</span>
            <input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={160}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 outline-none focus:border-indigo-400" />
          </label>
        )}
        {(modelId || pcb) && (
          <label className="block text-sm">
            <span className="mb-1 block text-xs font-semibold text-slate-600">{pcb ? 'Version de la carte, comme sur la sérigraphie (ex. V1.0)' : 'Ce qui change (facultatif)'}</span>
            <input value={label} onChange={(event) => setLabel(event.target.value)} maxLength={160} placeholder={pcb ? 'V1.0' : 'Perçages repris, matière changée…'}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 outline-none focus:border-indigo-400" />
          </label>
        )}
        {file && needsUnit(file.name) && (
          <label className="block text-sm">
            <span className="mb-1 block text-xs font-semibold text-slate-600">Unité du fichier (un STL ou un OBJ ne la donne pas)</span>
            <select value={unit} onChange={(event) => setUnit(event.target.value)} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2">
              {UNITS.map(([value, name]) => <option key={value} value={value}>{name}</option>)}
            </select>
          </label>
        )}
        {progress !== null && (
          <div className="h-2 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full bg-indigo-500 transition-all" style={{ width: `${Math.round(progress)}%` }} />
          </div>
        )}
        {error && <p className="text-sm text-rose-600">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-xl px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100">Annuler</button>
          <button type="submit" disabled={!file || progress !== null}
            className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">
            {progress !== null ? `Envoi… ${Math.round(progress)} %` : 'Déposer'}
          </button>
        </div>
      </form>
    </div>
  );
};

const Status = ({ version }) => {
  if (!version) return null;
  if (version.status === 'converting') return <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">Conversion…</span>;
  if (version.status === 'error') return <span className="rounded-full bg-rose-50 px-2 py-0.5 text-xs font-semibold text-rose-700">Conversion impossible</span>;
  return null;
};

const ModelsCard = ({ projectId, pcb = false }) => {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [adding, setAdding] = useState(false);
  const load = useCallback(() => modelsApi.list(projectId)
    .then((data) => setItems(data.items.filter((item) => (item.kind === 'pcb') === pcb)))
    .catch(() => setItems([])), [projectId, pcb]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!items?.some((item) => item.latest?.status === 'converting')) return undefined;
    const timer = setTimeout(load, 3000);
    return () => clearTimeout(timer);
  }, [items, load]);
  return (
    <Card title={pcb ? 'Cartes électroniques' : 'Modèles 3D et jumeaux'} icon={pcb ? Cpu : Box} action={(
      <button type="button" onClick={() => setAdding(true)}
        className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-700">
        <Plus className="h-3.5 w-3.5" /> Déposer
      </button>
    )}>
      {items === null && <div className="h-16 animate-pulse rounded-xl bg-slate-100" />}
      {items?.length === 0 && <Empty>{pcb
        ? 'Aucune carte : « Déposer » le zip du projet KiCad et de ses exports (schéma PDF, routage, STEP, fabrication).'
        : 'Aucun modèle 3D ni jumeau : « Déposer » un STEP, GLB, STL, OBJ ou une page HTML.'}</Empty>}
      {items?.length > 0 && (
        <ul className="divide-y divide-slate-100 rounded-xl border border-slate-200">
          {items.map((item) => {
            const Icon = { twin: Sparkles, pcb: Cpu }[item.kind] || Box;
            return (
              <li key={item.id}>
                <a href={`/project/${projectId}/modeles/${item.id}`} className="flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-slate-50">
                  <span className="flex min-w-0 items-center gap-2">
                    <Icon size={16} className="shrink-0 text-slate-400" />
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-semibold text-slate-800">{item.title}</span>
                      <span className="block text-xs text-slate-500">
                        {{ twin: 'Jumeau numérique', pcb: `Carte électronique${item.latest?.label ? ` · ${item.latest.label}` : ''}` }[item.kind] || `Modèle 3D · ${(item.latest?.source_format || '').toUpperCase()}`} · v{item.latest?.number} · {day(item.updated_at)}
                        {item.open_comments ? ` · ${item.open_comments} commentaire${item.open_comments > 1 ? 's' : ''} ouvert${item.open_comments > 1 ? 's' : ''}` : ''}
                      </span>
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-1.5">
                    <Status version={item.latest} />
                    {item.share && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">Client : v{item.share.version_number}</span>}
                  </span>
                </a>
              </li>
            );
          })}
        </ul>
      )}
      {adding && (
        <UploadDialog projectId={projectId} pcb={pcb} onClose={() => setAdding(false)}
          onDone={(made) => { setAdding(false); navigate(`/project/${projectId}/modeles/${made.model_id}`); }} />
      )}
    </Card>
  );
};

export default ModelsCard;
